import { getSegmentsLength } from 'components/TemplateEditor/Editor';
import { CustomSlateNodeType } from 'components/TemplateEditor/types';
import { Formik, FormikConfig, FormikProps } from 'formik';
import { permissionNameIdMap } from 'hooks/business/useNewPermisson';
import { ReactNode } from 'react';
import { useTranslate } from '@tolgee/react';
import { Element } from 'react-scroll';
import { platformExecuteContentLimitLengthMap } from 'services/config/platform';
import { Descendant } from 'slate';
import { StyledForm } from 'views/Aide/Scenario/PolicyForm';
import RegionExecuteContextsField from 'views/Aide/Scenario/PolicyForm/NoRegionForm/RegionExecuteContextsField';
import TermOfValidityField from 'views/Aide/Scenario/PolicyForm/TermOfValidityField';
import StoreScopesField from 'views/Aide/Scenario/TaskForm/StoreScopesField';
import CouponNum from './CouponNum';
import EnableField from './EnableField';
import KeywordListField from './KeywordListField';
import NameField from './NameField';
import { TaskFormValues } from './share';
import { useReactScrollContext } from 'views/Aide/components/ReactScrollContext';

type FV = TaskFormValues;

export interface TaskFormProps extends FormikConfig<FV> {
  platform: General.PlatformUpperCase;
  disabled?: boolean;
  children: (form: ReactNode, formProps: FormikProps<FV>) => ReactNode;
}

function TaskForm(props: TaskFormProps) {
  const { platform, disabled = false, children, ...otherProps } = props;

  const { t } = useTranslate();

  const { basicId } = useReactScrollContext();

  const isEdit = otherProps.initialValues.taskId !== undefined;

  return (
    <Formik enableReinitialize {...otherProps}>
      {(formProps) => {
        const form = (
          <StyledForm>
            <Element name={basicId}>
              <h3>{t('aide.基础配置')}</h3>
              <EnableField disabled={disabled} />
              <NameField disabled={disabled} />
              <StoreScopesField
                disabled={disabled}
                platform={platform}
                permId={permissionNameIdMap.超时自动回复}
              />
              <TermOfValidityField disabled={disabled} />
              <KeywordListField disabled={disabled} />
              <CouponNum disabled={disabled || isEdit} />
            </Element>
            <RegionExecuteContextsField
              disabled={disabled}
              scenarioType="SIGNAL_COUPON"
              platform={platform}
              validate={(value) => {
                if (!value) {
                  return t('refundSetting.请输入回复内容');
                }

                const {
                  executeContext: { content = [] },
                } = value;

                const hasText = content.some((v) => v.type === 'paragraph');

                if (!hasText) {
                  return t('aide.发送内容中缺少文本类型');
                }

                const hasConcept = (segments: Descendant[]): boolean => {
                  return segments.some((segment) => {
                    if (segment.type === CustomSlateNodeType.CONCEPT && segment.label === '券码') {
                      return true;
                    }

                    if (segment.type === CustomSlateNodeType.PARAGRAPH) {
                      return hasConcept(segment.children);
                    }

                    return false;
                  });
                };

                if (!hasConcept(content)) {
                  return t('aide.缺少“券码”参数');
                }

                const contentLength = getSegmentsLength(content, 'text');

                const { [platform]: contentLimit } = platformExecuteContentLimitLengthMap;

                if (contentLength > contentLimit) {
                  return t('refundSetting.回复内容不可超出x字', { x: contentLimit });
                }

                return undefined;
              }}
            />
          </StyledForm>
        );

        return children(form, formProps);
      }}
    </Formik>
  );
}

export default TaskForm;
